import { createFileRoute, Navigate } from "@tanstack/react-router";
import { z } from "zod";
import { useAuth0Login } from "~/hooks/useAuth0Login";
import { useAuthenticatedUser } from "~/hooks/useAuthenticatedUser";
import { useTenant } from "~/hooks/useTenant";
import { Spinner } from "~/components/ui/spinner";
import { useEffect, useState } from "react";
import { consumeJustLoggedOutFlag } from "~/lib/auth-recovery";

export const Route = createFileRoute("/$tenantSlug/login")({
  validateSearch: z.object({
    invitation: z.string().optional(),
    organization: z.string().optional(),
    organization_name: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
  component: TenantLogin,
});

function TenantLogin() {
  const { tenantSlug } = Route.useParams();
  const { user, isLoading: isUserLoading, routing } = useAuthenticatedUser();
  const { isAuthenticated, isLoading: isAuthLoading, login } = useAuth0Login();
  const tenant = useTenant();
  const { invitation, organization, organization_name, error, error_description } = Route.useSearch();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [justLoggedOut, setJustLoggedOut] = useState(() => consumeJustLoggedOutFlag());

  const isLoading = isAuthLoading || isUserLoading;
  const isAlreadyAuthenticated = !isLoading && isAuthenticated && !!user;

  // Auto-redirect to Auth0 with tenant org context
  // Skip if we just completed a logout.
  useEffect(() => {
    if (error || isAlreadyAuthenticated || justLoggedOut) return;
    if (isAuthLoading || hasRedirected) return;
    setHasRedirected(true);

    if (invitation && organization) {
      login("signup", { organizationId: organization, invitation, returnTo: `/${tenantSlug}/dashboard?new=true` });
    } else {
      login("login", {
        ...(tenant.orgId && { organizationId: tenant.orgId }),
        returnTo: `/${tenantSlug}/dashboard`,
      });
    }
  }, [error, isAlreadyAuthenticated, justLoggedOut, isAuthLoading, hasRedirected, login, invitation, organization, tenant.orgId, tenantSlug]);

  // If Auth0 returned an error, redirect to index which shows the error page
  if (error) {
    return (
      <Navigate
        to={`/`}
        search={{ error, error_description }}
      />
    );
  }

  if (isAlreadyAuthenticated) {
    return <Navigate to={(routing?.homePath ?? `/${tenantSlug}/dashboard`)} />;
  }

  // After a normal logout, show a logged-out screen with a button to log in again
  if (justLoggedOut) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-8">
        <h1 className="text-2xl font-bold text-black">You have been logged out</h1>
        <p className="text-black text-center max-w-md">
          You have been successfully logged out of your account.
        </p>
        <button
          type="button"
          onClick={() => {
            setJustLoggedOut(false);
            login("login", {
              forcePrompt: true,
              ...(tenant.orgId && { organizationId: tenant.orgId }),
              returnTo: `/${tenantSlug}/dashboard`,
            });
          }}
          className="inline-flex items-center justify-center rounded-md bg-brand-green-primary px-6 py-2.5 text-sm font-medium text-white hover:bg-brand-green-primary/90 transition-colors"
        >
          Log in again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4">
      <Spinner className="size-8 text-brand-green-primary" />
      {invitation && (
        <p className="text-sm text-muted-foreground">
          Accepting invitation to {organization_name || "organization"}...
        </p>
      )}
    </div>
  );
}
